import { useState, useEffect } from 'react';
import api from '../services/api';

const PERMISSIONS = [
    { key: 'voir_planning',       label: 'Voir le planning' },
    { key: 'creer_livraison',     label: 'Créer une livraison' },
    { key: 'modifier_livraison',  label: 'Modifier une livraison' },
    { key: 'supprimer_livraison', label: 'Supprimer une livraison' },
    { key: 'voir_patients',       label: 'Voir les patients' },
    { key: 'modifier_patients',   label: 'Modifier les patients' },
    { key: 'voir_recurrences',    label: 'Voir les récurrences' },
    { key: 'modifier_recurrences',label: 'Modifier les récurrences' },
    { key: 'voir_logs',           label: 'Voir les logs' },
    { key: 'bouton_alerte',       label: 'Bouton alerte SAP' },
    { key: 'voir_stats',          label: 'Voir les statistiques' },
    { key: 'voir_carte',          label: 'Voir la carte patients' },
];

const ROLES = ['ROLE_INF_DIET', 'ROLE_SECRETAIRE', 'ROLE_LOG', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'];

export default function AclPage() {
    const [aclId, setAclId] = useState(null);
    const [config, setConfig] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        loadAcl();
    }, []);

    const loadAcl = async () => {
        try {
            const res = await api.get('/acl_configs');
            const first = res.data.member?.[0];
            if (first) {
                setAclId(first.id);
                setConfig(first.config);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const toggle = (role, permission) => {
        setConfig(prev => ({
            ...prev,
            [role]: {
                ...prev[role],
                [permission]: !prev[role]?.[permission],
            }
        }));
        setSaved(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.patch(`/acl_configs/${aclId}`, { config }, {
                headers: { 'Content-Type': 'application/merge-patch+json' }
            });
            setSaved(true);
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={{ padding: '24px', color: '#888' }}>Chargement...</div>;

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: '700', color: 'var(--text-main)' }}>
                        <i className="ti ti-shield-lock" style={{ marginRight: '8px' }} />
                        Tableau des permissions
                    </h2>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>
                        Configurez les droits d'accès par rôle
                    </p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: saved ? '#16a34a' : 'var(--accent-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                    }}
                >
                    <i className={`ti ${saved ? 'ti-check' : 'ti-device-floppy'}`} style={{ marginRight: '6px' }} />
                    {saving ? 'Enregistrement...' : saved ? 'Enregistré !' : 'Enregistrer'}
                </button>
            </div>

            <div style={{
                backgroundColor: 'var(--card-bg)',
                borderRadius: '12px',
                overflow: 'auto',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                    <thead>
                    <tr style={{ backgroundColor: 'var(--card-bg-secondary)' }}>
                        <th style={{ ...thStyle, textAlign: 'left', minWidth: '200px' }}>Permission</th>
                        {ROLES.map(role => (
                            <th key={role} style={{ ...thStyle, textAlign: 'center', fontSize: '11px' }}>
                                {role.replace('ROLE_', '')}
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {PERMISSIONS.map(perm => (
                        <tr key={perm.key} style={{ borderTop: '1px solid var(--border-light)' }}>
                            <td style={{ ...tdStyle, fontWeight: '500', color: 'var(--text-main)' }}>
                                {perm.label}
                            </td>
                            {ROLES.map(role => {
                                const active = config[role]?.[perm.key] ?? false;
                                const isFixed = role === 'ROLE_SUPER_ADMIN' || role === 'ROLE_ADMIN';
                                return (
                                    <td key={role} style={{ ...tdStyle, textAlign: 'center' }}>
                                        <button
                                            onClick={() => !isFixed && toggle(role, perm.key)}
                                            style={{
                                                width: '28px',
                                                height: '28px',
                                                borderRadius: '6px',
                                                border: 'none',
                                                backgroundColor: active ? '#dcfce7' : '#fee2e2',
                                                color: active ? '#16a34a' : '#dc2626',
                                                cursor: isFixed ? 'not-allowed' : 'pointer',
                                                fontSize: '14px',
                                                opacity: isFixed ? 0.6 : 1,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            title={isFixed ? 'Non modifiable pour ce rôle' : ''}
                                        >
                                            <i className={`ti ${active ? 'ti-check' : 'ti-x'}`} />
                                        </button>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const thStyle = { padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' };
const tdStyle = { padding: '10px 16px', fontSize: '13px', color: 'var(--text-muted)' };